import { useSyncExternalStore } from 'react';

/**
 * Abnahme 0.58 F01: Rückweg bei Übergängen zwischen Werkzeugen. Aufgaben öffnen sich über dem aktuellen Werkzeug
 * (Schließen führt dorthin zurück); wer von einem Inhalt zu einem anderen Werkzeug springt (Notiz → Datei, Übersicht →
 * Thema), bekommt einen sichtbaren Weg zurück an genau die Stelle (Adresse samt Datei, Seite, Ordner und offener
 * Aufgabe). Nur in dieser Sitzung des Tabs (sessionStorage), höchstens fünf Einträge, keine Inhalte.
 */
export type ReturnKind = 'task' | 'page' | 'file' | 'topic' | 'overview' | 'tasks' | 'intake' | 'approvals' | 'event' | 'decision' | 'goal' | 'risk' | 'budget' | 'contact' | 'shift' | 'folder';
export interface ReturnEntry {
  href: string;
  projectId: string;
  kind: ReturnKind;
  at: number;
}

const KEY = 'werft.return';
const MAX_AGE = 60 * 60_000;
const listeners = new Set<() => void>();
let cache: ReturnEntry[] | null = null;

function read(): ReturnEntry[] {
  if (cache) return cache;
  try {
    const raw = JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as ReturnEntry[];
    cache = Array.isArray(raw) ? raw.filter((x) => typeof x?.href === 'string' && x.href.startsWith('/p/') && Date.now() - x.at < MAX_AGE) : [];
  } catch {
    cache = [];
  }
  return cache;
}
function write(next: ReturnEntry[]) {
  cache = next;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ohne Speicher: nur im Speicher dieses Tabs */
  }
  for (const l of listeners) l();
}
/** Werkzeug der aktuellen Adresse (für die Beschriftung des Rückwegs) */
function kindOfHere(): ReturnKind {
  const p = location.pathname;
  const open = new URLSearchParams(location.search).has('aufgabe');
  if (open) return 'task';
  if (p.endsWith('/uebersicht')) return 'overview';
  if (p.endsWith('/notizen')) return 'page';
  if (p.endsWith('/dateien')) return 'file';
  if (p.endsWith('/gespraeche')) return 'topic';
  if (p.endsWith('/eingang')) return 'intake';
  if (p.endsWith('/freigaben')) return new URLSearchParams(location.search).has('entscheidung') ? 'decision' : 'approvals';
  if (p.endsWith('/termine')) return 'event';
  if (p.endsWith('/budget')) return 'budget';
  if (p.endsWith('/personen')) return 'contact';
  if (p.endsWith('/einsaetze')) return 'shift';
  if (p.endsWith('/ziele')) return new URLSearchParams(location.search).has('risiko') ? 'risk' : 'goal';
  return 'tasks';
}
const here = () => `${location.pathname}${location.search}`;

/** Vor einem Sprung in ein anderes Werkzeug: die aktuelle Stelle merken */
export function rememberReturn(projectId: string, kind: ReturnKind = kindOfHere()) {
  const href = here();
  const rest = read().filter((x) => x.href !== href);
  write([...rest, { href, projectId, kind, at: Date.now() }].slice(-5));
}

/** Rückweg benutzt oder hinfällig: obersten Eintrag entfernen */
export function dropReturn(href?: string) {
  const all = read();
  write(href ? all.filter((x) => x.href !== href) : all.slice(0, -1));
}

/** Oberster Rückweg für dieses Projekt – nicht, wenn man schon dort ist */
export function useReturn(projectId: string, currentHref: string): ReturnEntry | null {
  const all = useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    read,
  );
  const top = all.at(-1);
  if (!top || top.projectId !== projectId || top.href === currentHref) return null;
  return top;
}

/** U04: Rückweg sofort nehmen (z. B. „Kontakt anlegen“ aus dem Termin: nach dem Anlegen direkt zurück) */
export function takeReturn(projectId: string): ReturnEntry | null {
  const top = read().at(-1);
  if (!top || top.projectId !== projectId) return null;
  dropReturn(top.href);
  return top;
}

/** Ist man über Browser-Zurück schon an der gemerkten Stelle, ist der Eintrag erledigt */
export function dropArrived(projectId: string, currentHref: string) {
  const top = read().at(-1);
  if (top && top.projectId === projectId && top.href === currentHref) dropReturn(top.href);
}
